import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeSlash, Lock, Trash } from "@phosphor-icons/react";
import { PROVIDERS } from "./shared";

export function ApiKeysTab() {
  const { api } = useAuth();
  const [savedKeys, setSavedKeys] = useState({});
  const [keyInputs, setKeyInputs] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [saving, setSaving] = useState({});
  const [keyValidation, setKeyValidation] = useState({});
  const [replacingKey, setReplacingKey] = useState({});

  const loadKeys = useCallback(async () => {
    try {
      const resp = await api.get("/settings/keys");
      const map = {};
      resp.data.forEach((k) => { map[k.provider] = k.masked_key; });
      setSavedKeys(map);
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const saveKey = async (provider) => {
    const key = keyInputs[provider];
    if (!key) return;
    setSaving((s) => ({ ...s, [provider]: true }));
    setKeyValidation((s) => ({ ...s, [provider]: { status: "validating" } }));
    try {
      const resp = await api.post("/settings/keys", { provider, api_key: key });
      setSavedKeys((s) => ({ ...s, [provider]: resp.data.masked_key }));
      setKeyInputs((s) => ({ ...s, [provider]: "" }));
      setReplacingKey((s) => ({ ...s, [provider]: false }));
      setKeyValidation((s) => ({ ...s, [provider]: { status: "success" } }));
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to save key";
      let errorMsg = "This key is invalid. Check that you copied it correctly.";
      if (detail.includes("no credits")) errorMsg = "This key has no available credits. Add credits at openrouter.ai.";
      else if (detail.includes("Connection") || detail.includes("timed out")) errorMsg = "Could not reach OpenRouter. Check your connection and try again.";
      else if (detail.includes("Invalid")) errorMsg = "This key is invalid. Check that you copied it correctly.";
      setKeyValidation((s) => ({ ...s, [provider]: { status: "error", message: errorMsg } }));
    } finally {
      setSaving((s) => ({ ...s, [provider]: false }));
    }
  };

  const removeKey = async (provider) => {
    try {
      await api.delete(`/settings/keys/${provider}`);
      setSavedKeys((s) => { const n = { ...s }; delete n[provider]; return n; });
      toast.success(`${provider} key removed`);
    } catch { toast.error("Failed to remove key"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8" data-testid="apikeys-tab">
      <section>
        <h2 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-4">AI Provider API Keys</h2>
        <div className="space-y-4">
          {PROVIDERS.map((provider, idx) => (
            <motion.div key={provider.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.06 }}
              className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6" data-testid={`provider-card-${provider.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[var(--text-primary)] font-medium flex items-center gap-2">
                    {provider.name}
                    {savedKeys[provider.id] && !replacingKey[provider.id] && <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-['JetBrains_Mono']" data-testid={`key-active-badge-${provider.id}`}>Active</span>}
                  </h3>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">{provider.description}</p>
                </div>
                {savedKeys[provider.id] && !replacingKey[provider.id] && (
                  <button onClick={() => removeKey(provider.id)} className="p-2 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`remove-key-${provider.id}`}>
                    <Trash size={16} />
                  </button>
                )}
              </div>

              {savedKeys[provider.id] && !replacingKey[provider.id] ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--bg-base)] border border-[var(--border)]">
                    <Lock size={14} className="text-[var(--success)]" />
                    <span className="text-sm text-[var(--text-secondary)] font-['JetBrains_Mono']" data-testid={`masked-key-${provider.id}`}>{savedKeys[provider.id]}</span>
                  </div>
                  <button
                    onClick={() => { setReplacingKey((s) => ({ ...s, [provider.id]: true })); setKeyValidation((s) => ({ ...s, [provider.id]: null })); }}
                    className="text-xs text-[var(--text-muted)] hover:text-[#FF4400] transition-colors"
                    data-testid={`replace-key-${provider.id}`}
                  >
                    Replace Key
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input type={showKeys[provider.id] ? "text" : "password"} value={keyInputs[provider.id] || ""}
                        onChange={(e) => { setKeyInputs((s) => ({ ...s, [provider.id]: e.target.value })); setKeyValidation((s) => ({ ...s, [provider.id]: null })); }}
                        placeholder={`Enter ${provider.name} API key${provider.prefix ? ` (${provider.prefix}...)` : ""}`}
                        className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] pr-10"
                        data-testid={`input-key-${provider.id}`} />
                      <button type="button" onClick={() => setShowKeys((s) => ({ ...s, [provider.id]: !s[provider.id] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid={`toggle-key-${provider.id}`}>
                        {showKeys[provider.id] ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <Button onClick={() => saveKey(provider.id)} disabled={!keyInputs[provider.id] || saving[provider.id]}
                      className="bg-[#FF4400] hover:bg-[#FF6633] text-white min-w-[130px]" data-testid={`save-key-${provider.id}`}>
                      {saving[provider.id] ? (
                        <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Validating...</span>
                      ) : "Validate & Save"}
                    </Button>
                  </div>
                  {replacingKey[provider.id] && (
                    <button
                      onClick={() => { setReplacingKey((s) => ({ ...s, [provider.id]: false })); setKeyValidation((s) => ({ ...s, [provider.id]: null })); }}
                      className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                      data-testid={`cancel-replace-${provider.id}`}
                    >
                      Cancel
                    </button>
                  )}
                  {keyValidation[provider.id]?.status === "success" && (
                    <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid={`key-valid-${provider.id}`}>
                      <span className="w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-[10px]">&#10003;</span>
                      Key validated and saved.
                    </div>
                  )}
                  {keyValidation[provider.id]?.status === "error" && (
                    <div className="flex items-center gap-2 text-xs text-red-400" data-testid={`key-invalid-${provider.id}`}>
                      <span className="w-4 h-4 flex items-center justify-center rounded-full bg-red-500/20 text-red-400 text-[10px]">&#10007;</span>
                      {keyValidation[provider.id].message}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}
